// Qwen's duplex ASR protocol behind the application's transcription stream contract. The application owns
// permission, microphone capture, silence detection, retries and where recognized text is delivered.
const MODEL = 'qwen-audio-3.1-asr-flash-streaming';
const SENTENCE_SILENCE_MS = 800;

/**
 * A transcriber consumes PCM16 mono at sampleRate and emits ready, partial, final, failed and closed events.
 * finish() permits final results to drain; abort() detaches callbacks and immediately closes the stream.
 * Provider-specific language/hotword support and channel-based inputs are not assumed by this contract.
 */
export const qwenTranscriber = {
  input: 'pcm',
  open({ sampleRate, language, vocabulary, office, event }) {
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/stt/realtime?model=${MODEL}&room=${encodeURIComponent(office ?? '')}`);
    ws.binaryType = 'arraybuffer';
    const task = crypto.randomUUID().replace(/-/g, '');
    let ready = false, detached = false;
    const emit = (value) => { if (!detached) event(value); };
    ws.onopen = () => {
      const parameters = { format: 'pcm', sample_rate: sampleRate, max_sentence_silence: SENTENCE_SILENCE_MS };
      if (language) parameters.language_hints = [language];
      const words = typeof vocabulary === 'function' ? vocabulary() : vocabulary;
      if (words) parameters.vocabulary = words;
      emit({ type: 'opened', hotwords: !!words });
      ws.send(JSON.stringify({ header: { action: 'run-task', task_id: task, streaming: 'duplex' },
        payload: { task_group: 'audio', task: 'asr', function: 'recognition', model: MODEL, parameters, input: {} } }));
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      let value; try { value = JSON.parse(e.data); } catch { return; }
      const kind = value.header?.event;
      if (kind === 'task-started') { ready = true; emit({ type: 'ready' }); }
      else if (kind === 'result-generated') {
        const sentence = value.payload?.output?.sentence;
        if (sentence && !sentence.heartbeat) emit({ type: sentence.sentence_end ? 'final' : 'partial', text: String(sentence.text ?? '').trim() });
      } else if (kind === 'task-finished') { try { ws.close(1000, 'finished'); } catch {} }
      else if (kind === 'task-failed') {
        emit({ type: 'failed', code: value.header?.error_code, message: value.header?.error_message });
        try { ws.close(1000, 'failed'); } catch {}
      }
    };
    ws.onclose = (e) => emit({ type: 'closed', code: e.code });
    return {
      write(pcm) { if (ready && ws.readyState === 1) { ws.send(pcm); return true; } return false; },
      finish() {
        try {
          if (ready && ws.readyState === 1) ws.send(JSON.stringify({ header: { action: 'finish-task', task_id: task, streaming: 'duplex' }, payload: { input: {} } }));
          else ws.close(1000, 'stop');
        } catch {}
      },
      close() { try { ws.close(1000, 'stop'); } catch {} },
      abort() { detached = true; ws.onopen = null; ws.onmessage = null; ws.onclose = null; try { ws.close(1000, 'provider changed'); } catch {} },
    };
  },
};
